// "Compile to .hef" (backend/app/api/hailo_compile.py): how a compile job reads to the person waiting for it.
import type { HailoCompileJob } from "../api/ml";

/** Still on its way: the page keeps asking until it is not. */
export const compileActive = (j: Pick<HailoCompileJob, "status">): boolean => ["queued", "sending", "running"].includes(j.status);

const STAGES: Record<string, string> = { parsing: "Reading the model", optimizing: "Quantising to INT8", compiling: "Compiling for the chip" };

/**
 * Whether opening Compile to .hef should show this job rather than start another: one still going, or a finished one of the
 * model version about to be compiled (so its download can be fetched again; "Compile again" starts a new one).
 */
export const compileReusable = (j: Pick<HailoCompileJob, "status" | "model_version">, version: number | null): boolean =>
  compileActive(j) || (j.status === "completed" && version !== null && j.model_version === version);

/** One line saying where the compile is. */
export function compileHeadline(j: HailoCompileJob): string {
  if (j.stalled && compileActive(j)) return "The compile service is not responding. It may be down: Cancel clears this, and you can try again once it is back.";
  switch (j.status) {
    case "queued":
      return j.queue_position ? `Waiting for the compiler: ${j.queue_position} ahead of you.` : "Waiting for the compiler.";
    case "sending": return "Sending the model to the compiler.";
    case "running": {
      const stage = STAGES[j.stage] ?? "Working";
      return j.cancel_requested ? "Stopping." : `${stage}: ${Math.round(compileProgress(j) * 100)}%`;
    }
    case "completed": return "Compiled. The .hef is ready to download.";
    case "cancelled": return "Cancelled.";
    default: return "The compile did not finish.";
  }
}

/** 0 to 1, for the bar. */
export const compileProgress = (j: Pick<HailoCompileJob, "status" | "progress">): number =>
  j.status === "completed" ? 1 : Math.max(0, Math.min(1, j.progress || 0));

/** What the download is saved as: the server names the zip, so this only guards against a missing name. */
export const compileFilename = (projectName: string, j: Pick<HailoCompileJob, "filename">): string =>
  `${(j.filename || projectName || "model").replace(/[^A-Za-z0-9._-]+/g, "_")}_hef.zip`;

/** How often to ask while a compile is active: it runs for the better part of an hour, so not often. */
export const POLL_MS = 4000;
